import { Suspense } from "react";
import npLogo from "assets/np-logo-icon.svg";
import { LoadingIndicator, PortfolioSelect, UserMenu } from "components";
import { PortfolioOption } from "components/PortfolioSelect/PortfolioSelect";
import {
  TOTAL_INVESTMENTS_OPTION_ID,
  useGetPortfolioOptions,
} from "hooks/useGetPortfolioOptions";
import { useNavigateToPortfolioTab } from "layouts/PortfolioNavigationHeaderLayout/PortfolioNavigationHeader/useNavigateToPortfolioTab";
import {
  Outlet,
  useParams,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { NavTab } from "./NavTab/NavTab";
import { NavTabPath } from "./NavTab/types";
import { useNavTab } from "./NavTab/useNavTab";

interface NavTabTemplateLayoutProps {
  routes: NavTabPath[];
}

export const NavTabLayout = ({ routes }: NavTabTemplateLayoutProps) => {
  const { tabsRef, groupProps, panelsProps } = useNavTab({
    navTabPaths: routes,
  });

  const {
    contactDbId,
    portfolioId: portfolioIdUrl,
    holdingId,
    transactionId,
  } = useParams();
  const navigate = useNavigate();
  const isSubpage = !!(holdingId || transactionId);

  const portfolioOptions = useGetPortfolioOptions();
  const portfolioId = portfolioIdUrl
    ? parseInt(portfolioIdUrl, 10)
    : TOTAL_INVESTMENTS_OPTION_ID;

  const navigateToPortfolioTab = useNavigateToPortfolioTab();
  const onPortfolioChange = (selectedOption: PortfolioOption) => {
    navigateToPortfolioTab(selectedOption.urlPrefix);
  };

  // get current nav tab
  const { pathname } = useLocation();
  const getActiveTab = () => {
    const tabValues = [
      "overview",
      "analytics",
      "holdings",
      "transactions",
      "documents ",
    ];

    return tabValues.indexOf(
      pathname.split("/").find((item) => tabValues.includes(item)) ?? ""
    );
  };

  // redirect to root when portfolioId does not match available portfolios
  if (
    portfolioId !== TOTAL_INVESTMENTS_OPTION_ID &&
    !portfolioOptions.some(
      (option) =>
        option.id === portfolioId ||
        //Extend this to a recursive operation to check subs of subs
        option?.subOptions?.some((subOption) => subOption.id === portfolioId)
    )
  ) {
    //handle impersonation mode
    if (contactDbId) return <Navigate to={`/impersonate/${contactDbId}/`} />;
    return <Navigate to="/" replace />;
  }

  return (
    <div className="flex overflow-auto flex-col flex-1 items-stretch px-2 md:px-0 w-full">
      <NavTab.Group {...groupProps} selectedIndex={getActiveTab()}>
        <div
          className={`${
            isSubpage && "hidden"
          } md:flex container flex-col-reverse lg:flex-row gap-2 lg:gap-0 items-center lg:items-end pt-2 lg:pt-4 md:pb-2 mx-auto`}
        >
          <div className="flex flex-row w-full">
            <div className="hidden lg:block mr-2 h-full">
              <img
                src={npLogo}
                alt="Navigator Partners Logo"
                className="object-contain cursor-pointer"
                onClick={() => {
                  let path = "/overview";
                  if (contactDbId) path = `/impersonate/${contactDbId}${path}`;
                  navigate(path);
                }}
              />
            </div>
            <NavTab.List>
              {routes
                .filter((route) => !route.isHidden)
                .map((route, index) => (
                  <NavTab.NavTab
                    key={`NavTab_${index}`}
                    ref={(el: HTMLButtonElement) =>
                      (tabsRef.current[index] = el)
                    }
                  >
                    {route.tabLabel}
                  </NavTab.NavTab>
                ))}
            </NavTab.List>
          </div>

          <div className="flex gap-2 lg:gap-4 justify-between items-center w-full h-fit py-2 lg:py-0 lg:w-fit">
            <div className="flex flex-row w-full">
              <div className="block lg:hidden h-full w-[48px]">
                <img
                  src={npLogo}
                  alt="Navigator Partners Logo"
                  className="object-contain"
                />
              </div>
              <div className="z-50 self-center w-full sm:min-w-[300px]">
                {portfolioOptions.length > 0 ? (
                  <div className="max-w-[300px]">
                    <PortfolioSelect
                      portfolioOptions={portfolioOptions}
                      portfolioId={portfolioId}
                      onChange={onPortfolioChange}
                    />
                  </div>
                ) : (
                  <div />
                )}
              </div>
            </div>
            <UserMenu />
          </div>
        </div>

        <Suspense fallback={<LoadingIndicator center />}>
          <NavTab.CarouselPanels {...panelsProps}>
            {routes.map((route, index) => (
              <NavTab.Panel key={index}>{route.tabComponent}</NavTab.Panel>
            ))}
          </NavTab.CarouselPanels>
        </Suspense>
      </NavTab.Group>
      <Outlet />
    </div>
  );
};
